/**
 * Starred messages — server-backed, private to this user, synced across devices.
 *
 *   const { starredIds, isStarred, toggleStar, starred, refresh } = useStarredMessages()
 *
 * Two shapes, because the UI needs two things:
 *   - `starredIds` — a Set for the per-bubble star icon, loaded once for the
 *     whole account rather than per thread. Threads are small and the set is
 *     just ids, so one fetch beats a lookup per conversation open.
 *   - `starred` — the hydrated rows the Starred page renders (sender, preview,
 *     conversation name) and uses to jump to a message.
 *
 * `toggleStar` is optimistic and rolls back on failure — a star that appears and
 * then silently isn't saved is worse than one that visibly snaps back.
 *
 * `dropStar` is the other shape: a one-way unstar for a message that is being
 * deleted. It never rolls back, because rolling back would briefly bring a
 * just-deleted message back into Starred, and the server's own starred list
 * already leaves deleted messages out regardless of whether the unstar call
 * itself succeeded — so a failed one here is harmless to ignore.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { useAuthStore } from '@/features/auth/store/auth.store'
import {
  listStarredMessages,
  starMessage,
  unstarMessage,
} from '@/sdk/instavcMessenger-instance'
import type { StarredMessage } from '@/sdk/instavcMessenger'

interface UseStarredMessages {
  /** Hydrated rows for the Starred page, newest-starred first. */
  starred: StarredMessage[]
  /** Ids only — for the star icon on each bubble. */
  starredIds: Set<string>
  isStarred: (messageId: string) => boolean
  toggleStar: (messageId: string) => Promise<void>
  /** Unstar without rollback — for a message that's being deleted. */
  dropStar: (messageId: string) => Promise<void>
  refresh: () => Promise<void>
  isLoading: boolean
  error: string | null
}

export function useStarredMessages(): UseStarredMessages {
  const me = useAuthStore((s) => s.user)
  const presenceId = useAuthStore((s) => s.presenceId)
  const [starred, setStarred] = useState<StarredMessage[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    if (!me?.id) return
    setIsLoading(true)
    setError(null)
    try {
      const page = await listStarredMessages(me.id, { limit: 200 })
      setStarred(page.items)
    } catch {
      setError('failed to load starred messages')
    } finally {
      setIsLoading(false)
    }
  }, [me?.id])

  useEffect(() => {
    if (!me?.id || !presenceId) return
    void load()
  }, [me?.id, presenceId, load])

  const starredIds = useMemo(
    () => new Set(starred.map((s) => s.messageId)),
    [starred],
  )

  const isStarred = useCallback((messageId: string) => starredIds.has(messageId), [starredIds])

  const toggleStar = useCallback(
    async (messageId: string) => {
      if (!me?.id) return
      const wasStarred = starredIds.has(messageId)
      const previous = starred

      // Optimistic. The placeholder row carries only the id — the real one
      // (sender, preview, conversation) arrives on the refresh below, which is
      // what the Starred page renders from.
      setStarred((prev) =>
        wasStarred
          ? prev.filter((s) => s.messageId !== messageId)
          : [
              {
                messageId,
                conversationId: null,
                conversationName: null,
                senderId: null,
                senderName: null,
                body: null,
                attachmentKind: null,
                createdAt: null,
                starredAt: new Date().toISOString(),
              },
              ...prev,
            ],
      )

      try {
        if (wasStarred) await unstarMessage(me.id, messageId)
        else {
          await starMessage(me.id, messageId)
          // Replace the placeholder with the server's hydrated row.
          void load()
        }
      } catch {
        setStarred(previous)
        setError(wasStarred ? 'failed to unstar' : 'failed to star')
      }
    },
    [me?.id, starred, starredIds, load],
  )

  const dropStar = useCallback(
    async (messageId: string) => {
      if (!me?.id) return
      setStarred((prev) => prev.filter((s) => s.messageId !== messageId))
      try {
        await unstarMessage(me.id, messageId)
      } catch {
        // No rollback and no error surfaced — see the note on `dropStar` above.
      }
    },
    [me?.id],
  )

  return { starred, starredIds, isStarred, toggleStar, dropStar, refresh: load, isLoading, error }
}
